/**
 * Refresh Token API
 *
 * Handles automatic token refresh when access token expires:
 * - Prevents multiple simultaneous refresh attempts
 * - Auto-logout on token expiration
 * - Graceful error handling
 */

import { buildApiUrl } from './config';

interface RefreshTokenResponse {
  message: string;
}

// Global state for refresh token management
let isRefreshing = false;
let refreshSubscribers: Array<(success: boolean) => void> = [];

/**
 * Subscribe to refresh token completion
 * Used to queue requests while refresh is in progress
 */
function subscribeTokenRefresh(callback: (success: boolean) => void): void {
  refreshSubscribers.push(callback);
}

/**
 * Notify all subscribers about refresh completion
 */
function notifyRefreshSubscribers(success: boolean): void {
  refreshSubscribers.forEach((callback) => callback(success));
  refreshSubscribers = [];
}

/**
 * Handle token expiration - logout and redirect to login
 */
function handleTokenExpiration(): void {
  console.warn('[Auth] Tokens expired, logging out...');

  // Clear any local storage
  if (typeof window !== 'undefined') {
    localStorage.clear();
    sessionStorage.clear();

    // Redirect to login page
    window.location.href = '/login?reason=session_expired';
  }
}

/**
 * Refresh access token using refresh token from cookies
 * Prevents multiple simultaneous refresh attempts
 */
export async function refreshAccessToken(): Promise<boolean> {
  // If already refreshing, wait for the current refresh to complete
  if (isRefreshing) {
    return new Promise<boolean>((resolve) => {
      subscribeTokenRefresh((success) => {
        resolve(success);
      });
    });
  }

  isRefreshing = true;

  try {
    const response = await fetch(buildApiUrl('/auth/refresh'), {
      method: 'POST',
      credentials: 'include', // Send refreshToken cookie
    });

    if (response.ok) {
      // Success - token refreshed
      console.log('[Auth] Token refreshed successfully');
      notifyRefreshSubscribers(true);
      return true;
    }

    // Token refresh failed
    if (response.status === 401) {
      console.error('[Auth] Refresh token invalid or expired');
      handleTokenExpiration();
    } else {
      console.error('[Auth] Failed to refresh token:', response.status);
    }

    notifyRefreshSubscribers(false);
    return false;
  } catch (error) {
    console.error('[Auth] Error refreshing token:', error);
    notifyRefreshSubscribers(false);
    return false;
  } finally {
    isRefreshing = false;
  }
}

/**
 * Fetch with automatic token refresh on 401
 *
 * This wrapper automatically retries the request with a refreshed token
 * if the initial request fails with 401 Unauthorized.
 *
 * @param url - The URL to fetch
 * @param options - Fetch options
 * @returns Response
 */
export async function fetchWithTokenRefresh(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  // Ensure credentials are included
  const fetchOptions: RequestInit = {
    ...options,
    credentials: 'include',
  };

  // First attempt
  let response = await fetch(url, fetchOptions);

  // If 401, try to refresh token and retry once
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();

    if (refreshed) {
      // Retry the original request with new token
      response = await fetch(url, fetchOptions);

      // If still 401 after refresh, handle expiration
      if (response.status === 401) {
        console.error('[Auth] Still unauthorized after token refresh');
        handleTokenExpiration();
      }
    }
    // If refresh failed, handleTokenExpiration already called in refreshAccessToken
  }

  return response;
}

/**
 * Check if user is authenticated
 * @returns true if user has valid tokens
 */
export function isAuthenticated(): boolean {
  // In HTTP-only cookie setup, we can't check client-side
  // This is a best-effort check
  return typeof window !== 'undefined' && !window.location.pathname.includes('/login');
}
